// ────────────────────────────────────────────────────────────────────
// The 254px asset sidebar — design_handoff/README.md:158-161.
//
// Assets grouped by client, each group headed by a colour-coded chip and the
// client's name; each row carries the asset id, its service line and a DUE
// line coloured by urgency. The selected row inverts. A footer note says why
// the list only ever grows.
//
// WHERE THE DUE LINE COMES FROM, and where it does not.
//
// Every asset in this list except the open one is a DIFFERENT asset, so its
// numbers are not on `GET /api/assets/{id}/integrity` — that record describes
// one asset. The due dates here are read from `GET /api/plants/{id}/compliance`
// (`schemas.ComplianceRow`), which resolves each asset's due server-side
// through `compliance/asset_due.resolve_asset_due` over the SAME
// `bulk_derived_months` the integrity machinery produces. `months_remaining`
// arrives already computed.
//
// So this file subtracts no dates, compares no rates and infers no interval.
// It maps a server value to a colour, which is the only thing the handoff asks
// the sidebar to decide. An asset with no resolvable due prints "no rate yet"
// rather than a fabricated month — README.md:160's `#a8a29e` case, and the same
// rule as the rate strip: never synthesise a date.
//
// COLOURS come from the palette (src/index.css). The handoff's four client
// codes — A #0369a1, B #6d28d9, C #15803d, D #c25f05 (README.md:86) — are
// --info, --violet, --ok and --accent; its urgency ramp — <2 yr #b91c1c,
// <4 yr #c25f05, else #78716c, no rate #a8a29e — is --err, --accent-deep
// (accent as TEXT is the deep tone), --ink-3 and --ink-4.
// ────────────────────────────────────────────────────────────────────
import { monthYear } from "./format";

/** The asset-row fields a sidebar entry prints. A structural subset of
 *  `lib/types.ts` Asset, exactly as `AssetIdentityFacts` is, so a caller hands
 *  its `Asset[]` straight in and this file never grows a second copy of the
 *  row. */
export interface SidebarAsset {
  id: number;
  tag: string;
  client_name: string;
  service: string;
  asset_class: string;
}

/** A subset of `schemas.ComplianceRow`. `months_remaining` and `state` are the
 *  server's, not this screen's. */
export interface SidebarDue {
  next_due: string;
  state: string;
  months_remaining: number | null;
}

/** The handoff's footer note, verbatim (XZYNEXA.dc.html). It is the reason the
 *  list is a list and not a search result: the asset outlives every job. */
export const SIDEBAR_FOOTER_NOTE =
  "An asset appears here the first time we sign anything on it, and it never leaves. "
  + "Jobs come and go against it.";

/** What a row with no resolvable due prints. Never a month. */
export const NO_RATE_YET = "no rate yet";

/** The handoff's four client codes, as palette tokens. Assigned by the
 *  client's position in the sorted group list, so a client keeps its colour
 *  for as long as the same set of clients is on screen. */
const CHIP_COLOURS = ["var(--info)", "var(--violet)", "var(--ok)", "var(--accent)"] as const;

/** Under 2 years is danger, under 4 is accent, beyond that is muted — and no
 *  rate at all is muted-2. README.md:160. The months arrive from the server;
 *  this only picks the ink. */
export function dueTone(due: SidebarDue | undefined): "none" | "danger" | "accent" | "muted" {
  if (!due || !due.next_due) return "none";
  if (due.state === "overdue") return "danger";
  const m = due.months_remaining;
  if (m == null) return "none";
  if (m < 24) return "danger";
  if (m < 48) return "accent";
  return "muted";
}

const TONE_COLOUR: Record<string, string> = {
  none:   "var(--ink-4)",
  danger: "var(--err)",
  accent: "var(--accent-deep)",
  muted:  "var(--ink-3)",
};

/** The due line's text. "no rate yet" where there is no date — the sidebar's
 *  form of the rate strip's em dash. */
export function dueLine(due: SidebarDue | undefined): string {
  if (!due || !due.next_due) return NO_RATE_YET;
  const when = monthYear(due.next_due);
  return due.state === "overdue" ? `OVERDUE · ${when}` : `DUE ${when}`;
}

/** Up to two initials, so a chip stays inside 17px. Presentation only — no
 *  client code exists on the asset row to print instead. */
function clientCode(client: string): string {
  const words = client.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

interface Group {
  client: string;
  colour: string;
  assets: SidebarAsset[];
}

/** Group by client, clients alphabetical, assets by tag inside each. Sorted
 *  rather than left in arrival order so the same plant always draws the same
 *  sidebar — a list that reshuffles between renders is one an engineer cannot
 *  build muscle memory on. */
export function groupByClient(assets: readonly SidebarAsset[]): Group[] {
  const by = new Map<string, SidebarAsset[]>();
  for (const a of assets) {
    const key = a.client_name || "—";
    const bucket = by.get(key);
    if (bucket) bucket.push(a);
    else by.set(key, [a]);
  }
  return [...by.entries()]
    .sort((x, y) => x[0].localeCompare(y[0]))
    .map(([client, rows], i) => ({
      client,
      colour: CHIP_COLOURS[i % CHIP_COLOURS.length],
      assets: [...rows].sort((x, y) => x.tag.localeCompare(y.tag)),
    }));
}

function AssetRow({ asset, due, selected, onSelect }: {
  asset: SidebarAsset;
  due: SidebarDue | undefined;
  selected: boolean;
  onSelect?: (assetId: number) => void;
}): JSX.Element {
  const tone = dueTone(due);
  return (
    // A real <button>: the row navigates, and a clickable <div> is unreachable
    // from a keyboard — the same reason the file cards in ./InspectionDetailPanel
    // are buttons.
    <button type="button" data-testid="sidebar-asset" data-asset-id={asset.id}
            data-selected={selected ? "true" : "false"} data-due-tone={tone}
            aria-current={selected ? "true" : undefined}
            onClick={() => onSelect?.(asset.id)}
            style={{
              display: "block", width: "100%", textAlign: "left", font: "inherit",
              cursor: onSelect ? "pointer" : "default",
              padding: "6px 7px", borderRadius: 6, border: "1px solid transparent",
              background: selected ? "var(--ink)" : "transparent",
            }}>
      <span className="font-mono"
            style={{
              display: "block", fontSize: 11.5, fontWeight: 600, letterSpacing: ".02em",
              color: selected ? "var(--paper-2)" : "var(--ink-1)",
            }}>
        {asset.tag}
      </span>
      <span style={{
        display: "block", fontSize: 10.5, marginTop: 1,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        color: selected ? "var(--paper-2)" : "var(--ink-3)",
        opacity: selected ? 0.8 : 1,
      }}>
        {asset.service || asset.asset_class || "—"}
      </span>
      <span className="font-mono"
            style={{
              display: "block", fontSize: 9.5, marginTop: 3,
              // The selected row is dark, so the urgency ramp would be
              // unreadable on it; the handoff replaces it with --accent-2
              // there (README.md:161) rather than dimming it.
              color: selected ? "var(--accent-2)" : TONE_COLOUR[tone],
            }}>
        {dueLine(due)}
      </span>
    </button>
  );
}

export function AssetSidebar({
  assets, dueByAsset, activeAssetId, onSelect, loading = false, emptyHint = "No assets on file.",
}: {
  assets: readonly SidebarAsset[];
  /** asset id → its resolved compliance row. Missing = no due resolved. */
  dueByAsset?: ReadonlyMap<number, SidebarDue>;
  activeAssetId?: number;
  onSelect?: (assetId: number) => void;
  loading?: boolean;
  emptyHint?: string;
}): JSX.Element {
  const groups = groupByClient(assets);
  return (
    <div data-testid="asset-sidebar"
         style={{
           width: 254, flex: "none", overflow: "auto",
           borderRight: "1px solid var(--line-2)",
           background: "var(--surface-2)",
           padding: "13px 11px 22px",
         }}>
      {loading && assets.length === 0 && (
        <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>Loading…</div>
      )}
      {!loading && assets.length === 0 && (
        <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>{emptyHint}</div>
      )}

      {groups.map((g) => (
        <div key={g.client} data-testid="sidebar-client-group" style={{ marginBottom: 15 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 3px 7px" }}>
            <span data-testid="client-chip" className="font-mono"
                  style={{
                    width: 17, height: 17, flex: "none", borderRadius: 4,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, fontWeight: 600, letterSpacing: ".02em",
                    color: g.colour,
                    // 12% tint of the chip's own colour, solid text (README.md:86).
                    background: `color-mix(in srgb, ${g.colour} 12%, transparent)`,
                  }}>
              {clientCode(g.client)}
            </span>
            <span className="font-mono"
                  style={{
                    fontSize: 9, fontWeight: 500, letterSpacing: ".12em", color: "var(--ink-4)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
              {g.client}
            </span>
          </div>
          {g.assets.map((a) => (
            <AssetRow key={a.id} asset={a} due={dueByAsset?.get(a.id)}
                      selected={a.id === activeAssetId} onSelect={onSelect} />
          ))}
        </div>
      ))}

      <div data-testid="sidebar-footer-note"
           style={{
             borderTop: "1px solid var(--hairline)", padding: "11px 4px 0",
             fontSize: 10.5, lineHeight: 1.6, color: "var(--ink-4)",
           }}>
        {SIDEBAR_FOOTER_NOTE}
      </div>
    </div>
  );
}
